import { recordMatch, fetchGameHistory, getStats } from '@bn-games/game-history-client';
import type { GameStats, MatchOutcome, MatchRecord } from '@bn-games/game-history-client';
import { opponentOf } from '../game/types.js';
import type { Color } from '../game/types.js';
import { isVariantId } from '../game/rules/index.js';
import { HISTORY_SCHEMA_VERSION } from './types.js';
import type { GameRecord, RecordedSeed } from './types.js';

/**
 * Write-side mapping: a finished local `GameRecord` -> the generic outcome
 * enum the server understands, from the recording player's own perspective.
 * `null` means "can't be determined from this record" — the caller skips the
 * sync entirely rather than guess.
 */
function computeOutcome(record: GameRecord): MatchOutcome | null {
  const localColor = record.seed.localColor;
  if (localColor === null) return null;
  if (record.result === 'draw') return 'draw';
  if (record.result === 'abandoned') return 'abandoned';
  // result === 'win'
  if (record.winner === null) return null;
  return record.winner === localColor ? 'win' : 'loss';
}

/**
 * Awaitable sync of one finished ONLINE record to the server, over
 * `recordMatch`'s own success/failure result — used both as the immediate
 * sync attempt and as the outbox's retry `send` callback (M7c). Routing
 * (whether this is even called, and whether the caller also writes
 * `localStorage`) is `commitFinishedRecord`'s job, not this function's — it
 * no longer checks `isLoggedIn()` or `record.seed.mode` itself.
 *
 * Returns `true` when the record should be considered delivered — including
 * the (should-be-unreachable for a real online match) case where no outcome
 * can be computed, since retrying that record would never succeed either.
 */
export async function syncOnlineRecord(record: GameRecord): Promise<boolean> {
  const outcome = computeOutcome(record);
  if (outcome === null) return true;
  const result = await recordMatch('checkers', {
    id: record.id,
    variant: record.variant,
    outcome,
    players: record.players,
    seed: record.seed,
    moves: record.moves,
    createdAt: record.createdAt,
    finishedAt: record.finishedAt,
  });
  return result.ok;
}

/**
 * Read-side inverse of `computeOutcome`. A loss is still a completed
 * `result: 'win'` record from the OTHER player's perspective — `GameRecord`'s
 * `result` field only distinguishes win/draw/abandoned, never "who" won
 * beyond `winner`.
 */
function outcomeToResult(
  outcome: MatchOutcome,
  localColor: Color,
): { result: 'win' | 'draw' | 'abandoned'; winner: Color | null } {
  if (outcome === 'draw') return { result: 'draw', winner: null };
  if (outcome === 'abandoned') return { result: 'abandoned', winner: null };
  if (outcome === 'win') return { result: 'win', winner: localColor };
  return { result: 'win', winner: opponentOf(localColor) };
}

/**
 * Light-touch structural validation, matching the local `HistoryStore`'s own
 * `isValid` philosophy (structural sanity, not exhaustive re-validation of a
 * payload this same client wrote in the first place).
 */
export function toRemoteGameRecord(remote: MatchRecord): GameRecord | null {
  if (!isVariantId(remote.variant)) return null;
  if (typeof remote.seed !== 'object' || remote.seed === null) return null;
  if (typeof remote.players !== 'object' || remote.players === null) return null;
  if (!Array.isArray(remote.moves)) return null;

  const seed = remote.seed as RecordedSeed;
  if (seed.localColor !== 'white' && seed.localColor !== 'black') return null;

  const { result, winner } = outcomeToResult(remote.outcome, seed.localColor);

  return {
    id: remote.id,
    version: HISTORY_SCHEMA_VERSION,
    createdAt: remote.createdAt,
    finishedAt: remote.finishedAt,
    mode: 'online',
    variant: remote.variant,
    players: remote.players as GameRecord['players'],
    winner,
    result,
    seed,
    moves: remote.moves as GameRecord['moves'],
  };
}

/** Best-effort fetch of the signed-in player's server-synced online history. */
export function fetchOnlineHistory(): Promise<GameRecord[]> {
  return fetchGameHistory('checkers', toRemoteGameRecord);
}

/**
 * This game's slice of `GET /history/stats`, for the account section's
 * one-line summary. `null` on any failure (network, signed-out, decode) —
 * the account section already only calls this while signed in, and treats a
 * missing stats line as "nothing to show" rather than an error.
 */
export async function fetchCheckersStats(): Promise<GameStats | null> {
  const result = await getStats();
  return result.ok ? result.value.byGame.checkers : null;
}
